import Cookies from 'js-cookie';

export interface IFMenuBase {
    key: string;
    title: string;
    component?: string;
    query?: string;
    requireAuth?: string;
    route?: string;
    /** 是否登录校验，true不进行校验（访客） */
    login?: boolean;
    minRole?: number;
    allowRoles?: number[];
}

// 获取 userRole
const userRole = parseInt(Cookies.get('userRole') || '0', 10);

const ROLE_PARTS = [1000, 100, 10, 2, 1];
const splitRole = (role: number) => {
    const result = new Set<number>();
    if (!role || role <= 0) {
        return result;
    }
    let remain = role;
    ROLE_PARTS.forEach((part) => {
        if (remain >= part) {
            remain -= part;
            result.add(part);
        }
    });
    if (remain !== 0) {
        result.clear();
        result.add(role);
    }
    return result;
};

const roleParts = splitRole(userRole);
const hasAnyRole = (allowRoles?: number[]) =>
    !allowRoles || allowRoles.some((role) => roleParts.has(role));
const hasMinRole = (minRole?: number) => !minRole || userRole >= minRole;

export interface IFMenu extends IFMenuBase {
    subs?: IFMenu[];
    icon?: string;
}

const filterMenuTree = (menu: IFMenu): IFMenu | null => {
    if (!hasMinRole(menu.minRole) || !hasAnyRole(menu.allowRoles)) {
        return null;
    }

    if (!menu.subs || menu.subs.length === 0) {
        return menu;
    }

    const filteredSubs = menu.subs
        .map((sub) => filterMenuTree(sub))
        .filter((item): item is IFMenu => item !== null);

    if (filteredSubs.length === 0 && !menu.component) {
        return null;
    }

    return { ...menu, subs: filteredSubs };
};

const menus: {
    menus: IFMenu[];
    others: IFMenu[] | [];
    [index: string]: any;
} = {
    menus: [
        { key: '/app/dashboard/index', title: '首页', component: 'Dashboard' },
        {
            key: '/app/admin',
            title: '管理员信息管理',
            minRole: 100,
            subs: [
                { key: '/app/admin/list', title: '管理员列表', component: 'AdminPage' },
                { key: '/app/admin/manager', title: '管理员管理', component: 'AdminManagerPage' },
            ],
        },
        {
            key: '/app/customer',
            title: '需求沟通信息',
            minRole: 100,
            subs: [
                {
                    key: '/app/customer/contact/list',
                    title: '客户沟通记录',
                    component: 'ContactPage',
                },
                {
                    key: '/app/customer/requirement/list',
                    title: '客户需求列表',
                    component: 'RequirementPage',
                },
                {
                    key: '/app/customer/requirement/new',
                    title: '新建需求',
                    component: 'RequirementManagerPage',
                    allowRoles: [100, 1000],
                },
            ],
        },
        {
            key: '/app/company',
            title: '客户信息管理',
            minRole: 10,
            subs: [
                { key: '/app/company/list', title: '客户列表', component: 'CompanyPage' },
                { key: '/app/company/manager', title: '添加客户', component: 'CompanyManagerPage' },
            ],
        },
        {
            key: '/app/customerManager',
            title: '客户经理管理',
            allowRoles: [1, 2, 100, 1000],
            subs: [
                {
                    key: '/app/customerManager/list',
                    title: '客户经理列表',
                    component: 'CustomerManagerListPage',
                    allowRoles: [1, 2, 100, 1000],
                },
                {
                    key: '/app/customerManager/manager',
                    title: '新建外部渠道',
                    component: 'CustomerManagerManagerPage',
                    allowRoles: [1, 100, 1000],
                },
            ],
        },
        {
            key: '/app/consultant',
            title: '顾问管理',
            allowRoles: [1, 2, 100, 1000],
            subs: [
                {
                    key: '/app/consultant/list',
                    title: '顾问列表',
                    component: 'ConsultantPage',
                    allowRoles: [1, 2, 100, 1000],
                },
                {
                    key: '/app/consultant/manager',
                    title: '新建顾问',
                    component: 'ConsultantManagerPage',
                    allowRoles: [1, 100, 1000],
                },
            ],
        },
        {
            key: '/app/project',
            title: '项目管理',
            allowRoles: [2, 100, 1000],
            subs: [
                {
                    key: '/app/project/list',
                    title: '项目列表',
                    component: 'ProjectPage',
                    allowRoles: [2, 100, 1000],
                },
                {
                    key: '/app/project/manager',
                    title: '新建项目',
                    component: 'ProjectManagerPage',
                    allowRoles: [2, 100, 1000],
                },
            ],
        },
        {
            key: '/app/my',
            title: '我的',
            minRole: 10,
            subs: [
                { key: '/app/my/company/list', title: '我的客户', component: 'CompanyPage' },
                { key: '/app/my/contact/list', title: '我的沟通记录', component: 'MyContactPage' },
                {
                    key: '/app/my/requirement/list',
                    title: '我的需求',
                    component: 'RequirementPage',
                },
                { key: '/app/my/project/list', title: '我的项目', component: 'ProjectPage' },
            ],
        },
        {
            key: '/app/settings',
            title: '系统设置',
            allowRoles: [100, 1000],
            subs: [
                {
                    key: '/app/settings/prompt',
                    title: '提示词版本管理',
                    component: 'PromptVersionPage',
                    allowRoles: [100, 1000],
                },
                {
                    key: '/app/settings/feedback',
                    title: '提示词反馈迭代',
                    component: 'PromptFeedbackPage',
                    allowRoles: [100, 1000],
                },
            ],
        },
        {
            key: '/app/mail',
            title: '批量邮件发送',
            allowRoles: [100, 1000],
            subs: [
                {
                    key: '/app/mail/accounts',
                    title: '邮箱账号',
                    component: 'MailAccountsPage',
                    allowRoles: [100, 1000],
                },
                {
                    key: '/app/mail/templates',
                    title: '邮件模板',
                    component: 'EmailTemplatesPage',
                    allowRoles: [100, 1000],
                },
                {
                    key: '/app/mail/recipient-lists',
                    title: '发送名单',
                    component: 'RecipientListsPage',
                    allowRoles: [100, 1000],
                },
                {
                    key: '/app/mail/batches',
                    title: '批量发送任务',
                    component: 'MailBatchesPage',
                    allowRoles: [100, 1000],
                },
                {
                    key: '/app/mail/operations',
                    title: '发送运行监控',
                    component: 'MailDispatchOperationsPage',
                    allowRoles: [100, 1000],
                },
                {
                    key: '/app/mail/inbound-monitor',
                    title: '邮箱回复监控',
                    component: 'InboundMonitorPage',
                    allowRoles: [100, 1000],
                },
            ],
        },
        {
            key: '/app/v2',
            title: 'V2版本',
            allowRoles: [1000],
            subs: [
                {
                    key: '/app/v2/workbench',
                    title: '工作台',
                    allowRoles: [1000],
                    subs: [
                        {
                            key: '/app/v2/workbench/index',
                            title: '待处理队列',
                            component: 'WorkbenchPage',
                        },
                        {
                            key: '/app/v2/workbench/tasks',
                            title: '我的任务',
                            component: 'TasksPage',
                        },
                    ],
                },
                {
                    key: '/app/v2/customer',
                    title: '客户',
                    allowRoles: [1000],
                    subs: [
                        {
                            key: '/app/v2/customer/companies',
                            title: '企业账户',
                            component: 'CompaniesPage',
                        },
                        {
                            key: '/app/v2/customer/persons',
                            title: '联系人',
                            component: 'PersonsPage',
                        },
                        {
                            key: '/app/v2/customer/communications',
                            title: '沟通记录',
                            component: 'CommunicationsPage',
                        },
                    ],
                },
                {
                    key: '/app/v2/development',
                    title: '开发与培育',
                    allowRoles: [1000],
                    subs: [
                        {
                            key: '/app/v2/development/overview',
                            title: '模块总览',
                            component: 'DevelopmentOverviewPage',
                        },
                        {
                            key: '/app/v2/development/signals',
                            title: '客户信号',
                            component: 'SignalsPage',
                        },
                    ],
                },
                {
                    key: '/app/v2/opportunity',
                    title: '商机',
                    allowRoles: [1000],
                    subs: [
                        {
                            key: '/app/v2/opportunity/list',
                            title: '商机列表 / 看板',
                            component: 'OpportunityPage',
                        },
                    ],
                },
                {
                    key: '/app/v2/project',
                    title: '项目',
                    allowRoles: [1000],
                    subs: [
                        {
                            key: '/app/v2/project/list',
                            title: '项目列表与执行计划',
                            component: 'ProjectV2Page',
                        },
                    ],
                },
                {
                    key: '/app/v2/resource',
                    title: '合作资源',
                    allowRoles: [1000],
                    subs: [
                        {
                            key: '/app/v2/resource/overview',
                            title: '模块总览',
                            component: 'ResourceOverviewPage',
                        },
                    ],
                },
                {
                    key: '/app/v2/analytics',
                    title: '经营分析',
                    allowRoles: [1000],
                    subs: [
                        {
                            key: '/app/v2/analytics/index',
                            title: '经营指标',
                            component: 'AnalyticsPage',
                        },
                    ],
                },
                {
                    key: '/app/v2/settings',
                    title: '系统管理',
                    allowRoles: [1000],
                    subs: [
                        {
                            key: '/app/v2/settings/overview',
                            title: '模块总览',
                            component: 'SettingsOverviewPage',
                        },
                        {
                            key: '/app/v2/settings/audit-events',
                            title: '审计日志',
                            component: 'AuditEventsPage',
                        },
                    ],
                },
            ],
        },
    ]
        .map((item) => filterMenuTree(item))
        .filter((item): item is IFMenu => item !== null),
    others: [
        {
            key: '/app/settings',
            title: '系统设置',
            allowRoles: [100, 1000],
            subs: [
                {
                    key: '/app/settings/prompt/view',
                    title: '查看提示词场景',
                    component: 'PromptVersionPage',
                    allowRoles: [100, 1000],
                },
            ],
        },
        {
            key: '/app/customer',
            title: '客户信息管理',
            minRole: 10,
            subs: [
                { key: '/app/customer/list', title: '联系人列表', component: 'CustomerPage' },
                {
                    key: '/app/customer/manager',
                    title: '联系人管理',
                    component: 'CustomerManagerPage',
                },
            ],
        },
        {
            key: '/app/customer',
            title: '沟通记录管理',
            minRole: 10,
            subs: [
                {
                    key: '/app/customer/contact/manager',
                    title: '沟通记录管理',
                    component: 'ContactManagerPage',
                },
            ],
        },
        {
            key: '/app/customer',
            title: '需求管理',
            minRole: 10,
            subs: [
                {
                    key: '/app/customer/requirement/manager',
                    title: '需求管理',
                    component: 'RequirementManagerPage',
                },
            ],
        },
        {
            key: '/app/customerManager',
            title: '客户经理管理',
            allowRoles: [1, 2, 100, 1000],
            subs: [
                {
                    key: '/app/customerManager/view',
                    title: '查看客户经理',
                    component: 'CustomerManagerManagerPage',
                    allowRoles: [1, 2, 100, 1000],
                },
            ],
        },
        {
            key: '/app/consultant',
            title: '顾问管理',
            allowRoles: [1, 2, 100, 1000],
            subs: [
                {
                    key: '/app/consultant/view',
                    title: '查看顾问',
                    component: 'ConsultantManagerPage',
                    allowRoles: [1, 2, 100, 1000],
                },
            ],
        },
        {
            key: '/app/project',
            title: '项目管理',
            allowRoles: [2, 10, 100, 1000],
            subs: [
                {
                    key: '/app/project/view',
                    title: '查看项目',
                    component: 'ProjectManagerPage',
                    allowRoles: [2, 10, 100, 1000],
                },
            ],
        },
    ]
        .map((item) => filterMenuTree(item))
        .filter((item): item is IFMenu => item !== null),
};

export default menus;
